import dayjs, { Dayjs } from "dayjs";
import {
  PurchaseOrderWeeklyRoute,
  PurchaseOrderWeeklySummaryData,
} from "../../types/purchase-order.type";

// ค่าคงที่และตัวช่วยล้วน ๆ ของมุมมอง "ตามสาย × วัน" ใน W7
// แกนวันคือ วันส่ง ของ PO (ตัดสินโดยเจ้าของงาน) สัปดาห์เริ่มวันจันทร์ตามที่ API กำหนด

/** จำนวนวันในสัปดาห์ของ API (จันทร์..อาทิตย์) */
export const WEEK_LENGTH = 7;

/** ตำแหน่งของวันอาทิตย์ใน `days` — แสดงเฉพาะเมื่อมี PO */
export const SUNDAY_INDEX = 6;

/** หัวคอลัมน์ของวัน เรียงตาม `days` ของ API (จันทร์ก่อน) */
export const WEEKDAY_SHORT_LABELS = ["จ", "อ", "พ", "พฤ", "ศ", "ส", "อา"];

/**
 * วันจันทร์ของสัปดาห์ที่มีวันนั้นอยู่ · dayjs นับอาทิตย์เป็นวันแรก (day() = 0) จึงคำนวณ
 * เองแทน startOf("week") ที่ขึ้นกับ locale
 */
export const snapToWeekStart = (date: Dayjs): Dayjs => {
  const day = date.startOf("day");
  const daysSinceMonday = (day.day() + 6) % WEEK_LENGTH;
  return day.subtract(daysSinceMonday, "day");
};

export const currentWeekStart = (): Dayjs => snapToWeekStart(dayjs());

/**
 * ตำแหน่งวันที่แสดงเป็นคอลัมน์ · จันทร์–เสาร์เสมอ ส่วนอาทิตย์เฉพาะเมื่อมี PO ส่งวันนั้น
 * (ร้านโมเดิร์นเทรดส่วนใหญ่ไม่รับของวันอาทิตย์ คอลัมน์ว่างทั้งแถวจึงเป็นแค่สิ่งรบกวน)
 */
export const visibleDayIndexes = (
  data: PurchaseOrderWeeklySummaryData,
): number[] => {
  const indexes = Array.from({ length: SUNDAY_INDEX }, (_, index) => index);
  const sundayCount = data.totals.cells[SUNDAY_INDEX]?.po_count ?? 0;
  if (sundayCount > 0) indexes.push(SUNDAY_INDEX);
  return indexes;
};

/**
 * เรียงแถวของสาย: ตามลำดับที่ API ส่งมา ยกเว้นแถว "ยังไม่จับคู่ร้าน" (route_number null)
 * ที่ย้ายไปไว้ท้ายสุดเสมอ
 */
export const orderWeeklyRoutes = (
  routes: PurchaseOrderWeeklyRoute[],
): PurchaseOrderWeeklyRoute[] => [
  ...routes.filter((route) => route.route_number !== null),
  ...routes.filter((route) => route.route_number === null),
];

/** ค่ามากสุดของ po_count ในทุกช่องสาย × วัน — ฐานของการไล่สีความหนาแน่น */
export const maxCellPoCount = (routes: PurchaseOrderWeeklyRoute[]): number =>
  routes.reduce(
    (max, route) =>
      route.cells.reduce((cellMax, cell) => Math.max(cellMax, cell.po_count), max),
    0,
  );

/** ความเข้มของสีพื้นช่อง 0..1 · ช่องว่างไม่มีสี ช่องที่มีของเริ่มที่อ่อนสุดที่ยังมองเห็น */
const MIN_HEAT = 0.15;
export const heatRatio = (count: number, max: number): number => {
  if (count <= 0 || max <= 0) return 0;
  return MIN_HEAT + (1 - MIN_HEAT) * (count / max);
};
